// 택배 API v1.0 문서 데이터
import { default_header, default_header_example } from '../defaultData';
import { apiParamDesc, apiDocDesc, apiMatrixTable } from '../docHelpers';

/**
 * 배송추적 조회(webhook) 의 결과 파일 예시.
 *
 * 문서 데이터에서 유일하게 자유구조(raw JSX) 로 남는 부분이다. 결과 파일은 웹훅 본문이 아니라
 * `result_file` URL 이 가리키는 별도 JSON 이라, 응답 예시 탭(`callback_example`)에 담으면
 * 업체가 웹훅 본문으로 오해한다. 파일 내용임이 드러나는 자리인 Description 에 코드 블록으로 둔다.
 */
const SYNC_TRACKING_RESULT_FILE_EXAMPLE = `[
  {
    "invoiceNo": "905072964441",
    "bundleNo": "PB260820085045",
    "statusId": "301",
    "statusName": "配完",
    "stationName": "台北營業所",
    "statusAt": "2026-08-24 15:00:00",
    "statusList": [
      {
        "statusId": "201",
        "statusName": "集貨",
        "stationName": "台北營業所",
        "statusAt": "2026-08-23 10:00:00"
      },
      {
        "statusId": "301",
        "statusName": "配完",
        "stationName": "台北營業所",
        "statusAt": "2026-08-24 15:00:00"
      }
    ],
    "errMsg": ""
  },
  {
    "invoiceNo": "905072964485",
    "bundleNo": "PB260820090204A",
    "errMsg": "배송 상태 정보가 없습니다."
  }
]`;

const getV10APIs = (userInfo) => {
  return [
    {
      title: '택배사 등록',
      url: '/doc/택배사 등록',
      data: [
        {
          version: 'v1.0',
          url: '/carriers',
          method: 'POST',
          desc: apiDocDesc({
            intro: '택배사에서 발급받은 계정 정보를 PLAYAUTO 솔루션에 등록합니다.',
            items: ['택배사와 직접 계약하여 발급받은 거래처 ID 와 인증코드가 필요합니다.'],
          }),
          header: default_header,
          header_example: default_header_example,
          body: [
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 거래처 ID',
                sub: '(택배사에서 발급받은 거래처 ID)',
              }),
            },
            {
              title: 'auth_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 인증코드',
                sub: '(택배사에서 발급받은 인증코드)',
              }),
            },
          ],
          body_example: {
            carrier_code: 'G005',
            carrier_id: 'playauto01',
            auth_code: 'AUTH0123456789',
          },
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'status',
              required: 'O',
              type: 'String',
              desc: '결과',
              indent: 1,
            },
            {
              title: 'messages',
              required: 'O',
              type: 'Array',
              desc: '처리 결과 메세지',
              indent: 1,
            },
          ],
          response_example: {
            results: [
              {
                status: 'success',
                messages: ['택배사 계정이 등록되었습니다.'],
              },
            ],
          },
          error_example: {
            error_code: 'e2305',
            messages: ['이미 등록되어 있는 택배사 계정입니다.'],
          },
        },
      ],
    },
    {
      title: '택배사 수정',
      url: '/doc/택배사 수정',
      data: [
        {
          version: 'v1.0',
          url: '/carriers',
          method: 'PATCH',
          desc: apiDocDesc({
            intro: 'PLAYAUTO 솔루션에 등록된 택배사 계정의 인증코드를 수정합니다.',
            items: [
              {
                text: '수정 가능한 항목은 인증코드(auth_code) 입니다.',
                sub: '택배사 코드(<code>carrier_code</code>)와 거래처 ID(<code>carrier_id</code>)는 수정할 계정을 지정하는 값이며 변경되지 않습니다.',
              },
              '입력한 인증코드로 택배사 계정 검증을 수행하며, 검증에 실패하면 수정되지 않습니다.',
            ],
          }),
          header: default_header,
          header_example: default_header_example,
          body: [
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 거래처 ID',
                sub: '(수정할 계정을 지정하는 값입니다. 이 값 자체는 수정되지 않습니다.)',
              }),
            },
            {
              title: 'auth_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '변경할 택배사 인증코드',
                sub: '(택배사에서 발급받은 인증코드)',
              }),
            },
          ],
          body_example: {
            carrier_code: 'G005',
            carrier_id: 'playauto01',
            auth_code: 'AUTH9876543210',
          },
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'status',
              required: 'O',
              type: 'String',
              desc: '결과',
              indent: 1,
            },
            {
              title: 'messages',
              required: 'O',
              type: 'String[]',
              desc: '처리 결과 메세지',
              indent: 1,
            },
          ],
          response_example: {
            results: [
              {
                status: 'success',
                messages: ['택배사 계정이 수정되었습니다.'],
              },
            ],
          },
          error_example: {
            error_code: 'e2301',
            messages: ['등록된 택배사 계정이 없습니다. (carrier_id: playauto01)'],
          },
        },
      ],
    },
    {
      title: '택배사 삭제',
      url: '/doc/택배사 삭제',
      data: [
        {
          version: 'v1.0',
          url: '/carriers',
          method: 'DELETE',
          desc: apiDocDesc({
            intro: 'PLAYAUTO 솔루션에 등록된 택배사 계정을 삭제합니다.',
            items: ['삭제할 계정은 택배사 코드(carrier_code)와 거래처 ID(carrier_id)로 지정합니다.'],
          }),
          header: default_header,
          header_example: default_header_example,
          body: [
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 거래처 ID',
                sub: '(삭제할 계정의 거래처 ID)',
              }),
            },
          ],
          body_example: {
            carrier_code: 'G005',
            carrier_id: 'playauto01',
          },
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'status',
              required: 'O',
              type: 'String',
              desc: '결과',
              indent: 1,
            },
            {
              title: 'messages',
              required: 'O',
              type: 'String[]',
              desc: '처리 결과 메세지',
              indent: 1,
            },
          ],
          response_example: {
            results: [
              {
                status: 'success',
                messages: ['택배사 계정이 삭제되었습니다.'],
              },
            ],
          },
          error_example: {
            error_code: 'e2301',
            messages: ['등록된 택배사 계정이 없습니다. (carrier_id: playauto01)'],
          },
        },
      ],
    },
    {
      title: '송장발번',
      url: '/doc/송장발번',
      data: [
        {
          version: 'v1.0',
          url: '/carriers/invoices',
          method: 'POST',
          desc: apiDocDesc({
            items: [
              '주문 리스트(orders)로 택배사 운송장번호를 발급하고 라벨(운송장 출력물) 출력까지 한 번에 처리합니다.',
              '작업을 등록하고 작업번호(work_no)를 즉시 반환합니다.<br />발번 결과는 <code>api_callback_url</code> 로 POST 방식으로 전송합니다.',
              '발번에 성공하면 라벨 URL 이 수신 데이터의 <code>invoice_url</code> 에 담깁니다.',
              {
                text: '솔루션에 등록된 주문을 조회하지 않습니다.',
                sub: '발송인·수취인·상품 정보를 요청에 모두 담아 보내주셔야 합니다.<br />묶음 식별자(<code>orders.uniq</code>)도 업체에서 부여하는 값이며 솔루션 주문번호와 무관합니다.',
              },
              '한번에 전송가능한 주문수는 최대 100건 입니다.',
              'api_callback_url 전송이 실패한 경우 5초 간격으로 최대 3회까지 재시도합니다.',
            ],
          }),
          header: default_header,
          header_example: default_header_example,
          body: [
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: '택배사 거래처 ID',
            },
            {
              title: 'orders',
              required: 'O',
              type: 'Object[]',
              desc: apiParamDesc({
                title: '발번할 주문 리스트',
                notes: ['한 번에 100건까지 요청 가능합니다.'],
              }),
            },
            {
              title: 'uniq',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '묶음 식별자',
                sub: '업체에서 부여하는 값이며 솔루션 주문번호와 무관합니다.<br />발번 결과 수신 데이터에 그대로 담겨 전달되므로 주문을 구분할 수 있는 값을 입력해주세요.',
              }),
              indent: 1,
            },
            {
              title: 'to_name',
              required: 'O',
              type: 'String',
              desc: '수취인명',
              indent: 1,
            },
            {
              title: 'to_tel',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '수취인 전화번호',
                sub: '(블랙캣(T-CAT) 이용시 to_tel, to_htel 중 하나는 필수)',
              }),
              indent: 1,
            },
            {
              title: 'to_htel',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '수취인 휴대전화번호',
                sub: '(블랙캣(T-CAT) 이용시 to_tel, to_htel 중 하나는 필수)',
              }),
              indent: 1,
            },
            {
              title: 'to_addr1',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '수취인 주소',
                sub: '우편번호는 택배사에서 처리하므로 입력하지 않습니다.',
              }),
              indent: 1,
            },
            {
              title: 'to_addr2',
              required: '',
              type: 'String',
              desc: '수취인 상세주소',
              indent: 1,
            },
            {
              title: 'ship_msg',
              required: '',
              type: 'String',
              desc: '배송 메시지',
              indent: 1,
            },
            {
              title: 'products',
              required: 'O',
              type: 'Object[]',
              desc: apiParamDesc({
                title: '상품 목록',
                sub: '1건 이상 입력해주세요.',
              }),
              indent: 1,
            },
            {
              title: 'shop_sale_name',
              required: 'O',
              type: 'String',
              desc: '상품명',
              indent: 2,
            },
            {
              title: 'sender_name',
              required: 'O',
              type: 'String',
              desc: '발송인명',
              indent: 1,
            },
            {
              title: 'sender_tel',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '발송인 전화번호',
                sub: '(블랙캣(T-CAT) 이용시 sender_tel, sender_htel 중 하나는 필수)',
              }),
              indent: 1,
            },
            {
              title: 'sender_htel',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '발송인 휴대전화번호',
                sub: '(블랙캣(T-CAT) 이용시 sender_tel, sender_htel 중 하나는 필수)',
              }),
              indent: 1,
            },
            {
              title: 'sender_addr1',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '발송인 주소',
                sub: '우편번호는 택배사에서 처리하므로 입력하지 않습니다.',
              }),
              indent: 1,
            },
            {
              title: 'sender_addr2',
              required: '',
              type: 'String',
              desc: '발송인 상세주소',
              indent: 1,
            },
            {
              title: 'carrier_extra_info',
              required: '',
              type: 'Object',
              desc: apiParamDesc({
                title: '택배사 전용 추가 옵션',
                sub: '미입력시 택배사 기본값이 적용됩니다.',
              }),
              indent: 1,
            },
            {
              title: 'print_obt_type',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '라벨타입 (T-Cat 전용)',
                defaultValue: '01',
                allowedValues: [
                  ['01', '<strong>1/2 A4 label</strong>'],
                  ['02', '<strong>1/3 A4 label</strong>'],
                  ['03', '<strong>Heat transfer label</strong>'],
                ]
              }),
              indent: 2,
            },
            {
              title: 'thermosphere',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '배송온도 (T-Cat 전용)',
                defaultValue: '0001',
                allowedValues: [
                  ['0001', '<strong>Ambient</strong>'],
                  ['0002', '<strong>Refrigerated</strong>'],
                  ['0003', '<strong>Frozen</strong>'],
                ]
              }),
              indent: 2,
            },
            {
              title: 'product_type_id',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '상품타입 (T-Cat 전용)',
                defaultValue: '0009',
                allowedValues: [
                  ['0001', '<strong>General food</strong>'],
                  ['0002', '<strong>Specialty/Sweet products</strong>'],
                  ['0003', '<strong>Alcohol/Oil/Vinegar/Sauce</strong>'],
                  ['0004', '<strong>Grains and vegetables</strong>'],
                  ['0005', '<strong>Seafood/Meat products</strong>'],
                  ['0006', '<strong>3C (Computers, Communication, ConsumerElectronics)</strong>'],
                  ['0007', '<strong>Home appliances</strong>'],
                  ['0008', '<strong>Clothing accessories</strong>'],
                  ['0009', '<strong>Household products</strong>'],
                  ['0010', '<strong>Beauty and cosmetics</strong>'],
                  ['0011', '<strong>Health food </strong>'],
                  ['0012', '<strong>Medical-related supplies</strong>'],
                  ['0013', '<strong>Pet supplies/Feed</strong>'],
                  ['0014', '<strong>Printed matter</strong>'],
                  ['0015', '<strong>Others</strong>'],
                ]
              }),
              indent: 2,
            },
            {
              title: 'spec',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '화물규격 (T-Cat 전용)',
                defaultValue: '0001',
                allowedValues: [
                  ['0001', '<strong>60cm</strong>'],
                  ['0002', '<strong>90cm</strong>'],
                  ['0003', '<strong>120cm</strong>'],
                  ['0004', '<strong>150cm</strong>'],
                ]
              }),
              indent: 2,
            },
            {
              title: 'api_callback_url',
              required: '',
              type: 'String',
              desc: apiParamDesc({
                title: '작업완료시 작업결과를 수신받을 URL',
                sub: '해당정보 입력시 작업결과를 입력하신 주소로 POST 방식으로 전송합니다.',
              }),
            },
          ],
          body_example: {
            carrier_code: 'G005',
            carrier_id: '2800622302',
            orders: [
              {
                uniq: 'PB0068902239',
                to_name: '謝巧蓮',
                to_tel: '',
                to_htel: '0935467237',
                to_addr1: '台南市六甲區 復興街71號',
                to_addr2: '',
                ship_msg: '',
                products: [
                  { shop_sale_name: '日用品 1' },
                  { shop_sale_name: '日用品 2' },
                ],
                sender_name: 'ATOMY倉庫',
                sender_tel: '0800-586-685',
                sender_htel: '0910-123-456',
                sender_addr1: '桃園市楊梅區楊湖路1段159號',
                sender_addr2: '',
                carrier_extra_info: {
                  print_obt_type: '01',
                  thermosphere: '0001',
                  product_type_id: '0009',
                  spec: '0001',
                },
              },
              {
                uniq: 'PB0068902240',
                to_name: '林大明',
                to_tel: '',
                to_htel: '0912345678',
                to_addr1: '台北市中正區 忠孝東路1段1號',
                to_addr2: '',
                ship_msg: '',
                products: [{ shop_sale_name: '日用品 3' }],
                sender_name: 'ATOMY倉庫',
                sender_tel: '0800-586-685',
                sender_htel: '0910-123-456',
                sender_addr1: '桃園市楊梅區楊湖路1段159號',
                sender_addr2: '',
              },
            ],
            api_callback_url: 'https://test.io/callback',
          },
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'work_no',
              required: 'O',
              type: 'Number',
              desc: '등록된 작업번호',
              indent: 1,
            },
            {
              title: 'messages',
              required: 'O',
              type: 'String[]',
              desc: '결과 메세지',
              indent: 1,
            },
          ],
          response_example: {
            results: [
              {
                work_no: 36022757,
                messages: ['작업이 정상적으로 등록되었습니다.'],
              },
            ],
          },
          // api_callback_url 설정시 수신되는 데이터
          callback_example: {
            work_no: 36022757,
            job_name: '택배사 운송장 발번',
            carrier_code: 'G005',
            results: [
              {
                uniq: 'PB0068902239',
                invoice_no: '123456789012',
                invoice_url: 'https://carrier-api.playauto.org/label/123456789012.pdf',
                status: 'success',
                message: '',
              },
              {
                uniq: 'PB0068902240',
                invoice_no: '',
                invoice_url: '',
                status: 'fail',
                message: '이미 운송장번호가 발급된 주문입니다. (987654321098)',
              },
            ],
          },
          error_example: {
            work_no: 0,
            error_code: 'e2301',
            messages: ['등록된 택배사 계정이 없습니다. 택배사 등록 API로 계정을 먼저 등록해주십시오.'],
          },
        },
      ],
    },
    {
      title: '배송추적 조회',
      url: '/doc/배송추적 조회',
      data: [
        {
          version: 'v1.0',
          url: '/carriers/invoices/tracking',
          method: 'POST',
          desc: apiDocDesc({
            intro: '운송장번호로 택배사의 배송 상태와 배송 이력을 조회합니다.',
            items: [
              '작업을 등록하지 않고 조회 결과를 응답으로 즉시 반환합니다.<br />작업번호(<code>work_no</code>)와 결과 수신 URL(<code>api_callback_url</code>)을 사용하지 않습니다.',
              '한번에 조회가능한 운송장번호는 최대 10건 입니다.',
              {
                text: '운송장번호 단위로 성공·실패가 나뉩니다.',
                sub: '일부 운송장번호의 조회가 실패해도 나머지 결과는 정상 응답됩니다.<br />실패한 항목은 <code>status</code> 가 <code>fail</code> 이며 상태값이 빈 값으로, 사유가 <code>error_message</code> 에 전달됩니다.',
              },
              '상태코드·상태명·상태 변경일시는 택배사에서 전달한 원본 값입니다.',
            ],
          }),
          header: default_header,
          header_example: default_header_example,
          body: [
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: '택배사 거래처 ID',
            },
            {
              title: 'invoice_nos',
              required: 'O',
              type: 'String[]',
              desc: apiParamDesc({
                title: '조회할 운송장번호 리스트',
                notes: ['한 번에 10건까지 조회 가능합니다.'],
              }),
            },
          ],
          body_example: {
            carrier_code: 'G005',
            carrier_id: '2800622302',
            invoice_nos: ['907080200244', '907080200299'],
          },
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'invoice_no',
              required: 'O',
              type: 'String',
              desc: '운송장번호',
              indent: 1,
            },
            {
              title: 'uniq',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '묶음 식별자',
                sub: '송장발번 요청시 입력한 <code>orders.uniq</code> 값입니다.<br />조회에 실패한 경우 빈 값으로 전달됩니다.',
              }),
              indent: 1,
            },
            {
              title: 'status_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '배송 상태코드',
                sub: '택배사 원본 상태코드입니다. (예: 301 배송완료, 151 배송중, 111 환적중)',
              }),
              indent: 1,
            },
            {
              title: 'status_name',
              required: 'O',
              type: 'String',
              desc: '배송 상태명',
              indent: 1,
            },
            {
              title: 'status_date',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '상태 변경일시',
                sub: '택배사에서 전달한 값을 가공하지 않고 그대로 전달합니다. (YYYY-MM-DD HH:mm:ss)',
              }),
              indent: 1,
            },
            {
              title: 'station_name',
              required: 'O',
              type: 'String',
              desc: '처리 지점명',
              indent: 1,
            },
            {
              title: 'status_list',
              required: '',
              type: 'Object[]',
              desc: apiParamDesc({
                title: '배송 상태 이력',
                sub: '택배사에서 이력을 전달한 경우에만 포함되며 최신순으로 전달됩니다.<br />상위의 상태 필드는 이력의 최신 항목과 동일한 값입니다.',
              }),
              indent: 1,
            },
            {
              title: 'status_code',
              required: 'O',
              type: 'String',
              desc: '배송 상태코드',
              indent: 2,
            },
            {
              title: 'status_name',
              required: 'O',
              type: 'String',
              desc: '배송 상태명',
              indent: 2,
            },
            {
              title: 'status_date',
              required: 'O',
              type: 'String',
              desc: '상태 변경일시',
              indent: 2,
            },
            {
              title: 'station_name',
              required: 'O',
              type: 'String',
              desc: '처리 지점명',
              indent: 2,
            },
            {
              title: 'status',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '조회 결과',
                allowedValues: [
                  ['success', '조회 성공'],
                  ['fail', '조회 실패'],
                ],
              }),
              indent: 1,
            },
            {
              title: 'error_message',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '조회 실패 사유',
                sub: '조회에 성공한 경우 빈 값으로 전달됩니다.',
              }),
              indent: 1,
            },
          ],
          response_example: {
            results: [
              {
                invoice_no: '907080200244',
                uniq: 'PB0068902239',
                status_code: '301',
                status_name: '配完',
                status_date: '2026-08-18 08:06:23',
                station_name: '潮州營業所',
                status_list: [
                  {
                    status_code: '301',
                    status_name: '配完',
                    status_date: '2026-08-18 08:06:23',
                    station_name: '潮州營業所',
                  },
                ],
                status: 'success',
                error_message: '',
              },
              {
                invoice_no: '907080200299',
                uniq: '',
                status_code: '',
                status_name: '',
                status_date: '',
                station_name: '',
                status: 'fail',
                error_message: '운송장 번호 확인 실패',
              },
            ],
          },
          error_example: {
            error_code: 'e2304',
            messages: [
              '블랙캣 OBTStatus 실패 : 執行失敗 -> 貨態查詢，失敗。原因：無貨態明細資訊! (SrvTranId: TN20250414278802)',
            ],
          },
        },
      ],
    },
    {
      title: '배송추적 조회(webhook)',
      url: '/doc/배송추적 조회(webhook)',
      data: [
        {
          version: 'v1.0',
          method: 'POST',
          // 업체가 호출하는 엔드포인트가 없는 수신 안내 문서다.
          // 엔드포인트 카드를 수신 URL 표기로 바꾸고 API TEST 영역을 빼기 위한 플래그다
          is_webhook: true,
          desc: (
            <>
              {apiDocDesc({
                intro:
                  '배송추적 일괄 동기화 작업의 결과를 업체가 등록한 <code>api_callback_url</code> 로 전송합니다.',
                items: [
                  {
                    text: '업체에서 호출하는 API 가 아닙니다.',
                    sub: '배치로 자동 실행되는 작업이며, 작업이 완료되면 등록된 수신 URL 로 POST 방식으로 결과를 전송합니다.<br />수신 URL 은 동기화 작업 등록 시점에 지정된 값이며 이 문서에서 변경할 수 없습니다.',
                  },
                  {
                    text: '배송추적 결과는 전송 본문에 담기지 않습니다.',
                    sub: '본문에는 결과 파일의 URL(<code>result_file</code>)만 담기므로, 해당 URL 을 내려받아 처리해주세요.',
                  },
                  '결과 파일은 JSON 배열이며 운송장번호 단위로 성공·실패가 나뉩니다.',
                  '전송이 실패한 경우 5초 간격으로 최대 3회까지 재시도합니다. (요청당 응답 대기 30초)',
                ],
              })}
              {apiMatrixTable({
                intro:
                  '<strong>결과 파일(<code>result_file</code>) 데이터 형식</strong>',
                headers: ['필드', '타입', '설명'],
                rows: [
                  ['<code>invoiceNo</code>', 'String', '운송장번호'],
                  [
                    '<code>bundleNo</code>',
                    'String',
                    '묶음 식별자<br />송장발번 요청시 입력한 <code>orders.uniq</code> 값입니다.',
                  ],
                  [
                    '<code>statusId</code>',
                    'String',
                    '배송 상태코드<br />택배사 원본 상태코드입니다. (예: 301 배송완료, 201 집화)',
                  ],
                  ['<code>statusName</code>', 'String', '배송 상태명'],
                  ['<code>stationName</code>', 'String', '처리 지점명'],
                  [
                    '<code>statusAt</code>',
                    'String',
                    '상태 변경일시<br />택배사에서 전달한 값을 가공하지 않고 그대로 전달합니다. (YYYY-MM-DD HH:mm:ss)',
                  ],
                  [
                    '<code>statusList</code>',
                    'Object[]',
                    '배송 상태 이력<br />택배사에서 이력을 전달한 경우에만 포함되며 오래된 순으로 전달됩니다.<br />상위의 상태 필드는 이력의 최신 항목과 동일한 값입니다.',
                  ],
                  ['<code>statusList[].statusId</code>', 'String', '이력 항목의 배송 상태코드'],
                  ['<code>statusList[].statusName</code>', 'String', '이력 항목의 배송 상태명'],
                  ['<code>statusList[].stationName</code>', 'String', '이력 항목의 처리 지점명'],
                  ['<code>statusList[].statusAt</code>', 'String', '이력 항목의 상태 변경일시'],
                  [
                    '<code>errMsg</code>',
                    'String',
                    '조회 실패 사유<br />조회에 성공한 경우 빈 값으로 전달됩니다.<br /><strong>조회에 실패한 항목은 상태 관련 필드가 포함되지 않습니다.</strong> <code>invoiceNo</code>·<code>bundleNo</code>·<code>errMsg</code> 만 전달되므로 필드 존재 여부를 확인해주세요.',
                  ],
                ],
              })}
              <div className='description'>결과 파일 예시</div>
              <pre className='prettyprint'>{SYNC_TRACKING_RESULT_FILE_EXAMPLE}</pre>
            </>
          ),
          response: [
            {
              title: 'results',
              required: 'O',
              type: 'Object[]',
              desc: '',
            },
            {
              title: 'work_no',
              required: 'O',
              type: 'Number',
              desc: '작업번호',
              indent: 1,
            },
            {
              title: 'job_name',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '작업명',
                sub: '배송추적 일괄 동기화 작업은 <code>택배사 배송추적 일괄 동기화</code> 로 전달됩니다.',
              }),
              indent: 1,
            },
            {
              title: 'carrier_code',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '택배사 코드',
                allowedValues: [['G005', '블랙캣(T-CAT)']],
              }),
              indent: 1,
            },
            {
              title: 'carrier_id',
              required: 'O',
              type: 'String',
              desc: '택배사 거래처 ID',
              indent: 1,
            },
            {
              title: 'result_file',
              required: 'O',
              type: 'String',
              desc: apiParamDesc({
                title: '결과 파일 URL',
                sub: '배송추적 결과가 담긴 JSON 파일의 주소입니다. 데이터 형식은 위 안내를 참고해주세요.',
                notes: [
                  '조회 결과가 없거나 작업이 실패한 경우 빈 값으로 전달됩니다.',
                ],
              }),
              indent: 1,
            },
          ],
          // 수신 데이터. 업체가 호출하는 응답이 아니라 웹훅 본문이므로 Api Callback 예시로 둔다
          callback_example: {
            results: [
              {
                work_no: 36022801,
                job_name: '택배사 배송추적 일괄 동기화',
                carrier_code: 'G005',
                carrier_id: '2800622302',
                result_file:
                  'https://s3-ap-northeast-2.amazonaws.com/gmp01/carrier_job/1284_31346f77d3d61a16.json',
              },
            ],
          },
        },
      ],
    },
  ];
};

export default getV10APIs;
